import {
  BrowserRouter,
  Link,
  NavLink,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { useEffect,  useRef, useState  } from "react";
import "./App.css";
import CompanyTimeline from "./components/CompanyTimeline";
import FeaturedProject, {
  featuredProjectConfig,
} from "./components/FeaturedProject";

import ZoomableImage from "./components/ZoomableImage";
import AccountMenu from "./components/AccountMenu";
/* ============================================================
   PRODUCTS
   Each product has its own page component (see PRODUCT PAGES
   below). To add a product:
   1. Add an entry here.
   2. Write a page component for it.
   3. Set `page` to that component.
   ============================================================ */
import {
  formatProjectDate,
  getProjectSpan,
  getTimeRanges,
  hasTimeframe,
} from "./utils/projectTime";


const products = [
  {
    name: "TayTanium PVP",
    path: "/pvp",
    category: "Multiplayer game",
    status: "Live",
    accent: "#ff6b57",
    mark: "PVP",
    description: "A deep and complex competitive multiplayer game.",
    tagline:
      "Competitive multiplayer with real depth. Outplay, outthink, and outlast everyone in the arena.",
    time: [{ start: "2025-05" }],
    tech: ["React", "WebSockets", "Python"],
    externalUrl: "https://pvp.TayTanium.com",
    externalLabel: "Play TayTanium PVP",
    image: "/images/pvp.png",
    page: PvpPage,
  },
  {
    name: "TayTanium Chat",
    path: "/chat",
    category: "Communication",
    status: "Beta",
    accent: "#5aa9ff",
    mark: "CHAT",
    description: "A straightforward platform for real-time conversation.",
    tagline:
      "Fast, simple, real-time conversation. No clutter, no noise. Just you and the people you're talking to.",
    time: [{ start: "2026-06" }],
    tech: ["React", "Node.js", "WebSockets", "Python"],
    externalUrl: "https://chat.TayTanium.com",
    externalLabel: "Open TayTanium Chat",
    page: ChatPage,
  },
  {
    name: "PSS",
    path: "/pss",
    category: "Integration",
    status: "In Development",
    accent: "#e0c34d",
    mark: "PSS",
    description:
      "A streaming interaction system connecting chat platforms to OBS.",
    tagline:
      "Connect your chat to OBS with rewards, overlays, sounds, TTS, and custom stream interactions.",
    time: [{ start: "2026-02", end: "2026-04" }],
    tech: ["Python", "WebSockets", "HTML/CSS"],
    page: PSSPage,
  },
  {
    name: "Plant Man",
    path: "/plant-man",
    category: "Unity game",
    status: "Past Project",
    accent: "#6fcf7f",
    mark: "PLANT",
    description: "A 2D platforming adventure built with Unity.",
    tagline:
      "A platforming adventure about mastering your abilities, adapting to change, and making your way through nine worlds.",
    time: [{ start: "2023-09", end: "2024-09" }],
    tech: ["Unity", "C#"],
    image: "/images/PMW.png",
    page: PlantManPage,
  },
  {
    name: "The Messengers",
    path: "/the-messengers",
    category: "Unity game",
    status: "Past Project",
    accent: "#bd6fcf",
    mark: "MESSENGERS",
    description: "A cooperative adventure game built around teamwork and connection.",
    tagline:
      "A cooperative adventure where two players use different abilities to explore, solve problems, and reconnect a divided world.", 
    time: [{ start: "2024-08", end: "2024-10" }],
    tech: ["Unity", "C#"],
    page: TheMessengersPage,
  },
  {
    name: "HSRealm",
    path: "/hsrealm",
    category: "Minecraft Plugin",
    status: "Live",
    accent: "#e27535",
    mark: "HSREALM",
    description: "Custom Minecraft plugins powering the HSRealm server.",
    tagline:
      "A custom Minecraft plugin suite powering HSRealm's economy, homes, combat, profiles, and player systems.",
    time: [{ start: "2025-04" }],
    tech: ["Java", "Paper API", "Gradle"],
    externalUrl: "https://hsrealm.com",
    externalLabel: "Visit HSRealm",
    image: "/images/hsrealmlogo.png",
    page: HSRealmPage,
  },
  {
    name: "TEP",
    path: "/tep",
    category: "Translation Tool",
    status: "Live",
    accent: "#b7e235",
    mark: "TEP",
    description: "A translator to the custom tep language.",
    tagline:
      "A tool that can translate english to the custom tep language and back, supporting capitals, custom characters, and more.",
    time: [{ start: "2024-05", end: "2024-07" }, { start: "2026-09", end: "2026-10"}],
    tech: ["HTML/CSS", "SQL", "Python"],
    externalUrl: "https://tep.TayTanium.com",
    externalLabel: "Translate Here",
    page: TEPPage,
  },
  {
    name: "EWAA",
    path: "/ewaa",
    category: "Word tool",
    status: "Live",
    accent: "#4fd1c5",
    mark: "EWAA",
    description: "Every Word's An Acronym. Turn any word into a phrase.",
    tagline:
      "Every word's an acronym. Look up any word and read the phrase its letters spell out, then follow every word in it to the next.",
    time: [{ start: "2026-10" }],
    tech: ["React", "Flask", "Python", "SQLite"],
    externalUrl: "https://ewaa.taytanium.com",
    externalLabel: "Try EWAA",
    page: EwaaPage,
  },
];

/* ============================================================
   SMALL SHARED COMPONENTS
   ============================================================ */

function Arrow({ diagonal = false }) {
  return (
    <svg
      className="arrow"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={diagonal ? "M7 17 17 7M8 7h9v9" : "M4 12h16m-6-6 6 6-6 6"} />
    </svg>
  );
}

function StatusBadge({ status, dotOnly = false }) {
  if (!status) return null;

  return (
    <span
      className={dotOnly ? "status-dot" : "status"}
      data-status={status.toLowerCase().replace(/\s+/g, "-")}
    >
      {!dotOnly && status}
    </span>
  );
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// Accepts "2024" or "2024-03". Parsed manually to avoid timezone surprises.
function TimeRange({ range }) {
  const start = formatProjectDate(range.start);
  const end = range.end ? formatProjectDate(range.end) : null;

  return (
    <span className="timeframe-range">
      <time dateTime={range.start}>{start}</time>
      {end !== start && (
        <>
          {" – "}
          {end ? <time dateTime={range.end}>{end}</time> : "Present"}
        </>
      )}
    </span>
  );
}

function ProjectTimeframe({ product, className = "", span = false }) {
  const ranges = span
    ? [getProjectSpan(product)].filter(Boolean)
    : getTimeRanges(product);

  if (!ranges.length) return null;

  return (
    <span className={`timeframe ${className}`}>
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M3 10h18M8 3v4M16 3v4" />
      </svg>
      <span>
        {ranges.map((range, index) => (
          <span key={`${range.start}-${index}`}>
            {index > 0 && ", "}
            <TimeRange range={range} />
          </span>
        ))}
      </span>
    </span>
  );
}
function TechList({ items, limit }) {
  if (!items?.length) return null;

  const visible = limit ? items.slice(0, limit) : items;
  const hidden = items.length - visible.length;

  return (
    <ul className="tech-list" aria-label="Technologies">
      {visible.map((item) => (
        <li key={item} className="tech-chip">
          {item}
        </li>
      ))}
      {hidden > 0 && (
        <li className="tech-chip tech-chip-more" title={items.slice(limit).join(", ")}>
          +{hidden}
        </li>
      )}
    </ul>
  );
}

// Turns a string into a stable number between 0 and 1.
// Same input always gives the same output, so each product has its own "random" seed.
function seedFrom(text, salt) {
  let hash = 2166136261 ^ salt;
  for (const char of text) {
    hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
  }
  return ((hash >>> 0) % 1000) / 1000;
}

function ProductVisual({ product, size = "card" }) {
  const seeds = {
    "--s1": seedFrom(product.name, 1),
    "--s2": seedFrom(product.name, 2),
    "--s3": seedFrom(product.name, 3),
    "--s4": seedFrom(product.name, 4),
  };

  return (
    <div
      className={`visual visual-${size}${product.imageNoExtra ? " visual-image-no-extra" : ""}`}
      style={seeds}
      aria-hidden="true"
    >
      {product.image ? (
        <img
          src={product.image}
          alt=""
          loading="lazy"
          className="visual-image"
        />
      ) : (
        <span className="visual-mark">{product.mark}</span>
      )}
    </div>
  );
}


function ProductAction({ product }) {
  if (!product.externalUrl) {
    if (product.status === "Past Project") {
      return <span className="button button-disabled">Unreleased</span>;
    }

    return <span className="button button-disabled">Coming soon</span>;
  }

  return (
    <a href={product.externalUrl} className="button button-primary">
      {product.externalLabel || "Open product"}
      <Arrow diagonal />
    </a>
  );
}
function Section({ eyebrow, title, intro, children }) {
  return (
    <section className="page-section">
      <header className="page-section-head">
        {eyebrow && <p className="eyebrow eyebrow-accent">{eyebrow}</p>}
        <h2>{title}</h2>
        {intro && <p>{intro}</p>}
      </header>
      {children}
    </section>
  );
}

function Stats({ items }) {
  return (
    <dl className="stats">
      {items.map((item) => (
        <div className="stat" key={item.label}>
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function FeatureGrid({ items }) {
  return (
    <div className="feature-grid">
      {items.map((item, index) => (
        <div className="feature" key={item.title}>
          <span className="feature-icon">
            {String(index + 1).padStart(2, "0")}
          </span>
          <h3>{item.title}</h3>
          <p>{item.text}</p>
        </div>
      ))}
    </div>
  );
}

/* ============================================================
   SITE CHROME
   ============================================================ */

function RouteEffects() {
  const { pathname } = useLocation();

  useEffect(() => {
    // "instant" avoids a slow smooth-scroll from the bottom on page change.
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });

    const product = products.find((item) => item.path === pathname);
    let title;

    if (product?.name) {
      title = product.name;
    } else {
      switch (pathname) {
        case "/":
          title = "";
          break;

        case "/about":
          title = "About";
          break;

        case "/photography":
          title = "Photography";
          break;

        default:
          title = "Page not found";
      }
    }

    document.title = title
      ? `${title} — TayTanium Software`
      : "TayTanium Software";
  }, [pathname]);

  return null;
}

function Header() {
  return (
    <header className="site-header">
      <div className="container header-inner">
        <Link to="/" className="brand" aria-label="TayTanium Software home">
          <img src="/logo.png" alt="TayTanium Software" />
        </Link>

        <div className="header-right">
          <nav className="site-nav" aria-label="Main navigation">
            <NavLink to="/" end>
              Home
            </NavLink>
            <NavLink to="/about">About</NavLink>
          </nav>
          <AccountMenu />
        </div>
      </div>
    </header>
  );
}

function FooterProducts() {
  const scrollRef = useRef(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;

    setCanScrollUp(el.scrollTop > 1);
    setCanScrollDown(
      el.scrollTop + el.clientHeight < el.scrollHeight - 1
    );
  };

  useEffect(() => {
    updateScrollState();

    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, []);

  const scroll = (amount) => {
    scrollRef.current?.scrollBy({
      top: amount,
      behavior: "smooth",
    });
  };

  return (
    <div className="footer-products">
      {canScrollUp && (
        <button
          className="footer-products-arrow"
          onClick={() => scroll(-100)}
          aria-label="Scroll products up"
        >
          <span>↑</span>
        </button>
      )}

      <div className="footer-products-scroll" ref={scrollRef}>
        <ul className="footer-product-list">
          {products.map((product) => (
            <li key={product.path}>
              <Link to={product.path}>{product.name}</Link>
            </li>
          ))}
        </ul>
      </div>

      {canScrollDown && (
        <button
          className="footer-products-arrow"
          onClick={() => scroll(100)}
          aria-label="Scroll products down"
        >
          <span>↓</span>
        </button>
      )}
    </div>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="container footer-inner">
        <div className="footer-brand">
          <img src="/logo.png" alt="TayTanium Software" />
          <p>Games and software built for fun, creativity, and connection.</p>
        </div>

        <div className="footer-links">
          <div>
            <p className="footer-heading">Products</p>
            <FooterProducts />
          </div>

          <div>
            <p className="footer-heading">Company</p>
            <ul>
              <li>
                <Link to="/">Home</Link>
              </li>
              <li>
                <Link to="/about">About</Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="container footer-bottom">
        <p>© {new Date().getFullYear()} TayTanium Software</p>
        <p>Built with care.</p>
      </div>
    </footer>
  );
}
/* ============================================================
   HOME
   ============================================================ */

function HomePage() {
  return (
    <>
      <section className="hero">
        <h1>
          Games, software,
          <br />
          <span className="hero-muted">and everything in between.</span>
        </h1>

        <p className="lead hero-lead">
          Software built for fun, creativity, and connection. From competitive
          multiplayer to real-time chat to handcrafted games.
        </p>

        <div className="actions">
          <a href="#products" className="button button-primary">
            Browse products <Arrow />
          </a>
          <Link to="/about" className="button button-secondary">
            About TayTanium
          </Link>
        </div>
      </section>

      <FeaturedProject
        config={featuredProjectConfig}
        products={products}
      />


      <section
        id="products"
        className="home-section"
        aria-labelledby="products-title"
      >
        <div className="section-heading">
          <div>
            <p className="eyebrow">Products</p>
            <h2 id="products-title">What we're building</h2>
          </div>
          <p>
            Pick one of the {products.length} projects and take a look.
          </p>
        </div>

        <div className="product-grid">
          {products.map((product) => (
            <Link
              key={product.path}
              to={product.path}
              className="product-card"
              style={{ "--accent": product.accent }}
            >
              <ProductVisual product={product} />

              <div className="card-body">
                <div className="card-meta">
                  <span className="eyebrow">{product.category}</span>
                  <StatusBadge status={product.status} />
                </div>
                <h3>{product.name}</h3>
                <p>{product.description}</p>
                <div className="card-details">
                  <ProjectTimeframe product={product} />
                  <TechList items={product.tech} limit={4} />
                </div>
              </div>

              <span className="card-link">
                Learn more <Arrow />
              </span>
            </Link>
          ))}
        </div>
      </section>
      <Link to="/about" className="about-card">
          <div>
            <p className="eyebrow">About</p>
            <h2>About TayTanium Software</h2>
            <p>The ideas and development behind our games and software.</p>
          </div>

          <span className="about-link">
            Get to know us <Arrow />
          </span>
        </Link>
      {/*
      <section className="home-section" aria-labelledby="values-title">
        <div className="section-heading">
          <div>
            <p className="eyebrow">How we work</p>
            <h2 id="values-title">Small studio, big ideas</h2>
          </div>
        </div>

        <div className="values">
          <div className="value">
            <span className="value-number">01</span>
            <h3>Built for fun</h3>
            <p>
              Everything starts with one question: would we actually enjoy
              using this?
            </p>
          </div>
          <div className="value">
            <span className="value-number">02</span>
            <h3>Made with care</h3>
            <p>
              Clean design, fast performance, and details that are easy to
              overlook but hard to miss.
            </p>
          </div>
          <div className="value">
            <span className="value-number">03</span>
            <h3>Always improving</h3>
            <p>
              Our products keep evolving with regular updates, new features,
              and plenty of fixes.
            </p>
          </div>
        </div>

        <Link to="/about" className="about-card">
          <div>
            <p className="eyebrow">About</p>
            <h2>About TayTanium Software</h2>
            <p>The ideas and development behind our games and software.</p>
          </div>

          <span className="about-link">
            Get to know us <Arrow />
          </span>
        </Link>
      </section> 
      */}
    </>
  );
}

/* ============================================================
   PRODUCT PAGE LAYOUT
   Shared wrapper: back link, hero, and closing call-to-action.
   Everything between is up to each individual page.
   ============================================================ */

function ProductLayout({ product, children }) {
  return (
    <article
      className="page product-page"
      style={{ "--accent": product.accent }}
    >
      <Link to="/" className="back-link">
        <span aria-hidden="true">←</span> All products
      </Link>

      <header className="product-hero">
        <div className="product-hero-copy">
          <div className="card-meta">
            <span className="eyebrow">{product.category}</span>
            <StatusBadge status={product.status} />
          </div>

          <h1>{product.name}</h1>
          <p className="lead">{product.tagline}</p>

          {(hasTimeframe(product) || product.tech?.length > 0) && (
            <dl className="project-facts">
              {hasTimeframe(product) && (
                <div>
                  <dt>When</dt>
                  <dd>
                    <ProjectTimeframe product={product} />
                  </dd>
                </div>
              )}
              {product.tech?.length > 0 && (
                <div>
                  <dt>Built with</dt>
                  <dd>
                    <TechList items={product.tech} />
                  </dd>
                </div>
              )}
            </dl>
          )}

          <div className="actions">
            <ProductAction product={product} />
          </div>
        </div>

        <ProductVisual product={product} size="large" />
      </header>

      {children}

      <section className="product-cta">
  <div>
    <h2>
      {product.externalUrl
        ? `Ready to try ${product.name}?`
        : product.status === "Past Project"
          ? `${product.name} is from the past.`
          : `${product.name} is on its way.`}
    </h2>
    <p>
      {product.externalUrl
        ? "Jump in now. It only takes a few seconds to get started."
        : product.status === "Past Project"
          ? "This project is no longer active, but it's still part of our story."
          : "It's still in development. Check back soon for updates."}
    </p>
  </div>
  <ProductAction product={product} />
</section>
    </article>
  );
}

/* ============================================================
   PRODUCT PAGES
   Each one is its own component, so customize freely.
   All content below is placeholder.
   ============================================================ */


function PvpPage({ product }) {
  return (
    <ProductLayout product={product}>
      <Stats
        items={[
          { label: "Players", value: "Multiplayer" },
          { label: "Core gameplay", value: "PVP" },
          { label: "Platform", value: "Chat + Web" },
          { label: "Current version", value: "3.1" },
        ]}
      />

      <Section eyebrow="Overview" title="Battle. Collect. Compete.">
        <div className="prose">
          <p>
            TayTanium PVP is a multiplayer game built around battling other
            players, collecting items, and building up your account over time.
          </p>

          <p>
            What started as a simple battle system has grown into a larger
            game with progression, items, pets, collectibles, events, and a
            live website connected to the game.
          </p>
        </div>
      </Section>

      <Section
        eyebrow="Gameplay"
        title="Simple at first. Deeper as you play."
        intro="The core game is easy to understand, but there's plenty to discover."
      >
        <FeatureGrid
          items={[
            {
              title: "Battle players",
              text: "Attack other players, build kill streaks, and try to stay alive.",
            },
            {
              title: "Build your collection",
              text: "Collect items, pets, plushies, charms, stickers, and more.",
            },
            {
              title: "Earn & progress",
              text: "Earn points and credits through battles and other activities, then use them to expand your account.",
            },
            {
              title: "Play your way",
              text: "Events, modifiers, crafting, quests, and other systems keep the game changing over time.",
            },
          ]}
        />
      </Section>

      <Section eyebrow="Watch" title="TayTanium PVP 3.0">
        <div className="video-embed">
          <iframe
            src="https://www.youtube.com/embed/EV7GPhqCaIA"
            title="TayTanium PVP 3.0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      </Section>

      <Section eyebrow="FAQ" title="Common questions">
        <div className="faq">
          <details>
            <summary>How do I play TayTanium PVP?</summary>
            <p>
              The game is primarily played through the PVP chat on TayTanium Chat, with the
              website providing stats, collections, leaderboards, and other
              features.
            </p>
          </details>

          <details>
            <summary>What happens when I battle someone?</summary>
            <p>
              Battles can result in a block, a kill, or a self-kill. Items and
              other game mechanics can affect the outcome.
            </p>
          </details>

          <details>
            <summary>Is there more to do than battle?</summary>
            <p>
              Yes. PVP includes progression systems such as collecting,
              crafting, pets, quests, events, and other activities.
            </p>
          </details>

          <details>
            <summary>Where can I see my progress?</summary>
            <p>
              The PVP website lets you view your stats, inventory,
              achievements, collections, and other account information.
            </p>
          </details>
        </div>
      </Section>
    </ProductLayout>
  );
}

function ChatPage({ product }) {
  return (
    <ProductLayout product={product}>
      <Stats
        items={[
          { label: "Delivery", value: "Real-time" },
          { label: "Chats", value: "Unlimited" },
          { label: "Platform", value: "Web" },
          { label: "Price", value: "Free" },
        ]}
      />

      <Section eyebrow="Overview" title="A place to talk, together.">
        <div className="prose">
          <p>
            TayTanium Chat brings people together through real-time
            communication, from quick direct messages to full communities
            built around servers, channels, and roles.
          </p>
          <p>
            Share messages, files, reactions, and more while keeping
            conversations organized with categories, permissions, and
            customizable server roles.
          </p>
        </div>
      </Section>

      <Section eyebrow="Features" title="Built for conversation and community">
        <FeatureGrid
          items={[
            {
              title: "Real-time messaging",
              text: "Messages arrive instantly, with support for reactions, read receipts, and more.",
            },
            {
              title: "Servers & channels",
              text: "Organize communities with servers, categories, and individual chats.",
            },
            {
              title: "Direct messages",
              text: "Talk one-on-one or keep conversations focused in dedicated group chats.",
            },
            {
              title: "Roles & permissions",
              text: "Control what members can see and do with customizable roles and permissions.",
            },
            {
              title: "Bots",
              text: "Add bots to servers to automate tasks and extend what your community can do.",
            },
            {
              title: "File uploads",
              text: "Share files directly in your conversations alongside your messages.",
            },
          ]}
        />
      </Section>

      <Section eyebrow="FAQ" title="Common questions">
        <div className="faq">
          <details>
            <summary>Is TayTanium Chat free?</summary>
            <p>
              Yes. TayTanium Chat is free to use.
            </p>
          </details>
          <details>
            <summary>Do I need to download anything?</summary>
            <p>
              No. TayTanium Chat runs right in your browser. Open the link
              and you're in.
            </p>
          </details>
          <details>
            <summary>What does "beta" mean?</summary>
            <p>
              It means we're still actively building and improving things.
              Expect regular updates as new features and improvements are
              added.
            </p>
          </details>
        </div>
      </Section>
    </ProductLayout>
  );
}

function PlantManPage({ product }) {
  return (
    <ProductLayout product={product}>
      <Stats
        items={[
          { label: "Engine", value: "Unity" },
          { label: "Genre", value: "Platformer" },
          { label: "Platform", value: "PC" },
          { label: "Release", value: "TBA" },
        ]}
      />

      <Section eyebrow="Overview" title="Plant Man's journey begins.">
        <div className="prose">
          <p>
            Plant Man is a 2D platforming adventure developed in Unity.
            Start with a full set of movement abilities, explore increasingly
            challenging worlds, and adapt as abilities are taken away along
            the way.
          </p>
          <p>
            Jump, dash, glide, wall-jump, and push your way through a series
            of environments before facing the challenges waiting at the end
            of the journey.
          </p>
        </div>

        <ZoomableImage
          images={[
            {
              path: "/images/plantman.png",
              altText: "Plant Man Title Picture",
              name: "Plant Man",
              description: "The main Plant Man cover art.",
            },
            {
              path: "/images/plantmantiles.png",
              altText: "'Plant Man' spelled in tiles.",
              name: "Plant Man Tiles",
              description: "Plant Man spelling out using the grassland tile set.",
            },
          ]}
          wrap={false}
          imageHeight={240}
        />
      </Section>

      <Section eyebrow="Gameplay" title="Master your abilities">
        <FeatureGrid
          items={[
            {
              title: "Movement abilities",
              text: "Use jumping, double-jumping, wall-jumping, dashing, gliding, and wall-pushing to navigate the world.",
            },
            {
              title: "Changing abilities",
              text: "Begin with every ability, then gradually lose them as you progress through the worlds.",
            },
            {
              title: "Nine worlds",
              text: "Travel through grasslands, swamps, jungles, savannas, deserts, rocklands, mountains, caves, and snow.",
            },
            {
              title: "Adapt as you progress",
              text: "Each world changes how you approach its challenges by taking away different movement abilities.",
            },
            {
              title: "Challenging encounters",
              text: "Face enemies, environmental hazards, and increasingly complex platforming challenges along the way.",
            },
            {
              title: "The Centipede",
              text: "Take on a multi-phase boss encounter featuring changing attacks, enemies, falling rocks, and an escape sequence.",
            },
          ]}
        />
      </Section>
      
     
      <Section eyebrow="Progression" title="Lose your abilities. Find them again.">
         <ZoomableImage
          path="/images/PMMap.png"
          alt="PLANT MAN Map"
          name="Plant Man Map"
          description="A detailed view of the game map used in Plant Man."
          wrap={true}
          location="right"
        />
        <div className="prose">
          <p>
            Plant Man begins the adventure with every movement ability
            available. As the journey continues, each world removes a
            different ability, forcing you to approach familiar movement
            mechanics in new ways.
          </p>
          <p>
            In the final snowy world, abilities begin returning at checkpoints.
            Once the full set is restored, the game opens up opportunities
            for harder challenges and additional exploration.
          </p>
        </div>
        
      </Section>

      <Section eyebrow="Devlog" title="Development progress">
        <ol className="timeline">
          <li className="timeline-item" data-state="done">
            <span className="timeline-label">Done</span>
            <h3>Core movement</h3>
            <p>
              The primary movement abilities and platforming systems were
              developed in Unity.
            </p>
          </li>

          <li className="timeline-item" data-state="done">
            <span className="timeline-label">Done</span>
            <h3>Gameplay systems</h3>
            <p>
              Worlds, ability progression, enemies, and boss mechanics were
              prototyped.
            </p>
          </li>

          <li className="timeline-item" data-state="planned">
            <span className="timeline-label">Planned</span>
            <h3>Further development</h3>
            <p>
              Additional art, levels, music, and gameplay development remain
              to be completed.
            </p>
          </li>
        </ol>
      </Section>
    </ProductLayout>
  );
}



function PSSPage({ product }) {
  return (
    <ProductLayout product={product}>
      <Stats
        items={[
          { label: "Platform", value: "Any" },
          { label: "Integrations", value: "4+" },
          { label: "Runtime", value: "Local" },
          { label: "Release", value: "TBA" },
        ]}
      />

      <Section eyebrow="Overview" title="Make your chat part of the stream.">
        <div className="prose">
          <p>
            PSS connects your chat platform to OBS, turning messages and
            viewer interactions into live events on your stream.
          </p>
          <p>
            Set up points, rewards, overlays, sound effects, text-to-speech,
            and other interactions, then connect the system to the chat
            platform your community already uses.
          </p>
        </div>
      </Section>

      <Section
        eyebrow="Features"
        title="Built for interactive streams"
        intro="Connect your community to your stream with customizable rewards, events, and overlays."
      >
        <FeatureGrid
          items={[
            {
              title: "Rewards",
              text: "Create custom rewards that viewers can redeem with points, triggering sounds, overlays, images, and other stream events.",
            },
            {
              title: "OBS overlays",
              text: "Send live events directly to your stream through customizable browser-based overlays.",
            },
            {
              title: "Sound effects",
              text: "Play custom sounds and effects when viewers interact with your stream.",
            },
            {
              title: "Text-to-speech",
              text: "Turn viewer messages and events into speech that plays directly through your stream setup.",
            },
            {
              title: "Points & predictions",
              text: "Give viewers points for participating and let them use those points for rewards and interactive predictions.",
            },
            {
              title: "Chat integrations",
              text: "Connect PSS to platforms including Discord, Pronto, TayTanium Chat, or your own custom integration.",
            },
            {
              title: "Custom events",
              text: "Build your own interactions and connect PSS to custom systems through its API and real-time event infrastructure.",
            },
          ]}
        />
      </Section>

      <Section eyebrow="FAQ" title="Common questions">
        <div className="faq">
          <details>
            <summary>What is PSS?</summary>
            <p>
              PSS is a streaming interaction system that connects chat
              platforms to OBS and lets viewer activity trigger customizable
              stream events.
            </p>
          </details>

          <details>
            <summary>What can viewers interact with?</summary>
            <p>
              Depending on how PSS is configured, viewers can earn points,
              redeem rewards, trigger sounds and overlays, use text-to-speech,
              participate in predictions, and interact with other custom
              stream features.
            </p>
          </details>

          <details>
            <summary>What chat platforms does PSS support?</summary>
            <p>
              PSS can integrate with platforms such as Discord, Pronto, and
              TayTanium Chat, with support for custom integrations through its
              API.
            </p>
          </details>

          <details>
            <summary>Does PSS work with OBS?</summary>
            <p>
              Yes. PSS is designed to send events and interactive content to
              OBS through its overlay system.
            </p>
          </details>

          <details>
            <summary>Is PSS available yet?</summary>
            <p>
              PSS is currently in development and is not yet generally
              available.
            </p>
          </details>
        </div>
      </Section>
    </ProductLayout>
  );
}

function TheMessengersPage({ product }) {
  return (
    <ProductLayout product={product}>
      <Stats
        items={[
          { label: "Engine", value: "Unity" },
          { label: "Players", value: "2" },
          { label: "Genre", value: "Co-op Adventure" },
          { label: "Origin", value: "Hackathon" },
        ]}
      />

      <Section eyebrow="Overview" title="Two players. One world.">
        <div className="prose">
          <p>
            The Messengers is a cooperative adventure game built around
            communication and teamwork. Two players explore a world divided
            between different Platomite civilizations, each with abilities
            the other player does not have.
          </p>
          <p>
            Work together to cross dangerous environments, solve challenges,
            and reconnect a civilization that has been separated for
            thousands of years.
          </p>
        </div>
      </Section>

      <Section eyebrow="Characters" title="Different strengths. One team.">
        <div className="prose">
          <h3>Flint</h3>
          <p>
            A seven-year-old Platomite from the Fuji. Flint can jump farther
            than normal, pass through heat barriers, and push or break boxes.
          </p>

          <h3>Lumi</h3>
          <p>
            An eight-year-old Platomite from the Lanorth. Lumi moves naturally
            across ice, can pass through cold barriers, and can temporarily
            freeze the air around herself.
          </p>
        </div>
      </Section>

      <Section eyebrow="Gameplay" title="You can't do it alone.">
        <FeatureGrid
          items={[
            /*{
              title: "Two unique characters",
              text: "Flint and Lumi each have different abilities, making cooperation essential to getting through the world.",
            },*/
            {
              title: "Work together",
              text: "Use both characters' abilities to overcome environmental obstacles and reach places neither could reach alone.",
            },
            {
              title: "A world divided",
              text: "Travel through icy, volcanic, and underground environments shaped by the different Platomite collectives.",
            },
            {
              title: "Letters",
              text: "Connect Platomites by finding people with shared interests and delivering messages between them.",
            },
            {
              title: "Explore the unknown",
              text: "Discover hidden areas, lost civilizations, and the events that changed the Platomite world.",
            },
            {
              title: "Built for connection",
              text: "The game is designed around cooperation, gradually encouraging players to communicate and work together.",
            },
          ]}
        />
      </Section>

      
      {/*  
      <Section eyebrow="The World" title="A civilization beneath the surface.">
        <div className="prose">
          <p>
            Thousands of years ago, a catastrophic experiment changed the
            planet. The surviving Platomites fled underground and eventually
            became separated into different collectives.
          </p>
          <p>
            The Lanorth adapted to the cold while the Fuji survived near the
            planet's heat. Neither believed the other still existed.
          </p>
          <p>
            Four thousand years later, Flint and Lumi meet by chance - and
            begin uncovering the history that separated their people.
          </p>
        </div>
      </Section>

      <Section eyebrow="The Journey" title="Reconnect the Platomites.">
        <ol className="timeline">
          <li className="timeline-item" data-state="done">
            <span className="timeline-label">Beginning</span>
            <h3>The meeting</h3>
            <p>
              Flint and Lumi encounter each other and discover that their
              worlds are not as separate as they believed.
            </p>
          </li>

          <li className="timeline-item" data-state="active">
            <span className="timeline-label">Adventure</span>
            <h3>The lost scroll</h3>
            <p>
              The two players enter the underground dungeons in search of a
              scroll containing clues about the mysterious freeze.
            </p>
          </li>

          <li className="timeline-item" data-state="planned">
            <span className="timeline-label">Connection</span>
            <h3>Deliver the messages</h3>
            <p>
              Find Platomites with shared interests and deliver letters between
              them to begin rebuilding connections between the collectives.
            </p>
          </li>

          <li className="timeline-item" data-state="planned">
            <span className="timeline-label">Finale</span>
            <h3>Unite the collectives</h3>
            <p>
              Bring the leaders of the Lanorth, Fuji, and Namore together and
              uncover the truth behind the catastrophe.
            </p>
          </li>
        </ol>
      </Section>
        */}
      <Section eyebrow="Development" title="From hackathon to adventure.">
        <div className="prose">
          <p>
            The Messengers originally began as a hackathon project before
            developing into a larger cooperative game concept.
          </p>
          <p>
            Its design grew around a simple idea: making progress is dependent on
            another person. Every character has something the other needs,
            making teamwork part of the game's core rather than an optional
            feature.
          </p>
        </div>
      </Section>
    </ProductLayout>
  );
}

function HSRealmPage({ product }) {
  return (
    <ProductLayout product={product}>
      <Stats
        items={[
          { label: "Platform", value: "Minecraft" },
          { label: "Server", value: "HSRealm" },
          { label: "Runtime", value: "Paper" },
          { label: "Language", value: "Java" },
        ]}
      />

      <Section eyebrow="Overview" title="Your school. Your story. Your realm.">
        <div className="prose">
          <p>
            HSRealm is a custom Minecraft server built around schools,
            communities, and player-driven gameplay. I built the server's
            core plugin systems to create an economy, player progression,
            teleportation, combat rules, profiles, and more.
          </p>
          <p>
            Rather than relying entirely on off-the-shelf plugins, HSRealm
            uses custom systems designed specifically around how the server
            operates.
          </p>
        </div>
      </Section>

      <Section eyebrow="What I Built" title="The systems behind HSRealm">
        <FeatureGrid
          items={[
            {
              title: "Economy",
              text: "HSREconomy provides PIX, player trading, trade-ins, an auction house, and purchasable upgrades.",
            },
            {
              title: "Homes & Teleportation",
              text: "HSRHomes handles homes, TPA, spawn, warmups, teleport history, and player home limits.",
            },
            {
              title: "Combat",
              text: "HSRCombat implements custom Crystal PvP rules, combat tagging, grace periods, friends, and combat restrictions.",
            },
            {
              title: "Player Profiles",
              text: "HSRProfiles provides in-game profiles where players can view their school, balance, statistics, and other information.",
            },
          ]}
        />
      </Section>

      <Section eyebrow="Engineering" title="Built around the server">
        <div className="prose">
          <p>
            The plugins share data and permissions with the wider HSRealm
            system instead of operating as isolated additions to the server.
          </p>
          <p>
            This allows features such as account-linked home limits,
            economy-based upgrades, player profiles, combat relationships,
            and other systems to work together as one platform.
          </p>
        </div>
      </Section>

      <Section eyebrow="Technology" title="A custom Java stack">
        <FeatureGrid
          items={[
            {
              title: "Java",
              text: "The core language used to build the HSRealm plugin suite.",
            },
            {
              title: "Paper API",
              text: "HSRealm runs on a modified Paper-based Minecraft server.",
            },
            {
              title: "Gradle",
              text: "Used to build and manage the plugin projects and their dependencies.",
            },
            {
              title: "Custom systems",
              text: "Core gameplay functionality was developed specifically for HSRealm rather than being assembled entirely from existing plugins.",
            },
          ]}
        />
      </Section>

      <Section eyebrow="Live Project" title="Built for an active community.">
        <div className="prose">
          <p>
            HSRealm launched with its founding schools on June 12, 2025,
            creating a shared Minecraft world where schools and players can
            build, trade, compete, and shape their own communities.
          </p>
          <p>
            The custom plugin infrastructure continues to provide the systems
            that keep that world running.
          </p>
        </div>
      </Section>
    </ProductLayout>
  );
}

function TEPPage({ product }) {
  return (
    <ProductLayout product={product}>
      <Stats
        items={[
          { label: "Type", value: "Translator" },
          { label: "Dictionary", value: "Dynamic" },
          { label: "Languages", value: "All" },
          { label: "Persistence", value: "Shared" },
        ]}
      />

      <Section eyebrow="Overview" title="Translate to Tep.">
        <div className="prose">
          <p>
            TEP is a custom language and translation tool built around a
            generated Tep dictionary. Translate between English and Tep,
            look up words, add new vocabulary, and work with Tep directly
            in your browser.
          </p>
          <p>
            The translator preserves punctuation and capitalization while
            supporting Tep's custom characters, separators, and case markers.
          </p>
        </div>
      </Section>

      <Section eyebrow="Features" title="More than a word converter.">
        <FeatureGrid
          items={[
            {
              title: "Two-way translation",
              text: "Translate English into Tep and Tep back into English using the same dictionary.",
            },
            {
              title: "Capitalization",
              text: "Capitalization is encoded separately so titles, uppercase words, and mixed-case words can be restored when translating back.",
            },
            {
              title: "Dynamic dictionary",
              text: "Unknown English words can be added to the dictionary automatically and assigned a unique Tep word.",
            },
            {
              title: "Dictionary search",
              text: "Search the dictionary by English word or Tep word and copy results directly.",
            },
          ]}
        />
      </Section>

      <Section eyebrow="Built for the Web" title="A small tool with a real backend.">
        <div className="prose">
          <p>
            TEP runs as a web application with a Python backend, SQLite
            dictionary, and JSON API. Translation requests, dictionary
            updates, searches, and statistics are handled through the API.
          </p>
          <p>
            The frontend also includes request limits, input validation,
            Unicode normalization, error handling, and support for copying
            translated text directly from the browser.
          </p>
        </div>
      </Section>
    </ProductLayout>
  );
}

function EwaaPage({ product }) {
  return (
    <ProductLayout product={product}>
      <Stats
        items={[
          { label: "Dictionary", value: "17,000+" },
          { label: "Format", value: "Acronyms" },
          { label: "Platform", value: "Web" },
          { label: "Started", value: "Oct 2026" },
        ]}
      />

      <Section eyebrow="Overview" title="Words within words.">
        <div className="prose">
          <p>
            EWAA stands for Every Word's An Acronym. Pick any word and it
            becomes a phrase where each letter starts a word that describes
            the original.
          </p>
          <p>
            These are playful backronyms, not the real origins of the words.
            Every phrase is also a set of links, so you can keep following
            the connections from one word to the next.
          </p>
        </div>
      </Section>

      <Section
        eyebrow="How it works"
        title="Choose a word. Read between the letters."
        intro="Start with a word you know, or let the random button pick one for you."
      >
        <FeatureGrid
          items={[
            {
              title: "Search any word",
              text: "Look up a word to see its phrase, letter by letter.",
            },
            {
              title: "Random word",
              text: "Not sure where to start? Jump to a random word and see what it spells out.",
            },
            {
              title: "Follow the links",
              text: "Every word in a phrase has its own page, so you can keep exploring.",
            },
            {
              title: "Report issues",
              text: "If a phrase could describe its word better, send a report and help improve the dictionary.",
            },
          ]}
        />
      </Section>

      <Section eyebrow="Behind the scenes" title="How the dictionary is built.">
        <div className="prose">
          <p>
            Phrases are generated with a local AI model, then scored by a
            second judging pass. Anything that scores too low is
            regenerated.
          </p>
          <p>
            Inappropriate words are filtered out of the expansions, and the
            dictionary keeps growing as new words are added and reported
            phrases are improved.
          </p>
        </div>
      </Section>

      <Section eyebrow="FAQ" title="Common questions">
        <div className="faq">
          <details>
            <summary>What does EWAA stand for?</summary>
            <p>Every Word's An Acronym.</p>
          </details>

          <details>
            <summary>Are the phrases the real origins of the words?</summary>
            <p>
              No. They are creatively constructed backronyms made for fun.
              They describe what a word means, but they are not historical
              origins.
            </p>
          </details>

          <details>
            <summary>How do I report a bad phrase?</summary>
            <p>
              Open any word's page and use the "Report an issue" option below
              its phrase. Tell us how the phrase could be better and we'll
              look into it.
            </p>
          </details>

          <details>
            <summary>Is the dictionary complete?</summary>
            <p>
              Not yet. It already has more than 17,000 words and keeps
              growing as new entries are added.
            </p>
          </details>
        </div>
      </Section>
    </ProductLayout>
  );
}

/* ============================================================
   OTHER PAGES
   ============================================================ */

function AboutPage() {
  return (
    <article className="page">
      <Link to="/" className="back-link">
        <span aria-hidden="true">←</span> Home
      </Link>

      <header className="page-header">
        <p className="eyebrow">About</p>
        <h1>TayTanium Software</h1>
        <p className="lead">
          Games and software built for fun, creativity, and connection.
        </p>
      </header>

      <CompanyTimeline
        products={products}
        maxMilestoneLanes={2}

      />

      <Section eyebrow="Our story" title="Why we build">
        <div className="prose">
          <p>
            TayTanium Software started as a way to bring our projects together,
            growing out of TayTanium PVP and expanding into games, software,
            tools, and experiments.
          </p>
          <p>
            We build things because we have ideas we want to bring to life -
            learning new technologies, solving problems, and improving each
            project along the way.
          </p>
        </div>
      </Section>


      <Section eyebrow="About us" title="How we build">
        <div className="faq">
          <details>
            <summary>What programming languages and technologies do you know?</summary>
            <p>
              I know Python, C#, C++, Java, JavaScript, TypeScript, HTML/CSS, and SQL. For technologies, I know React, Node.js, Ollama, Websockets, Rest API, and more.
            </p>
          </details>
          <details>
            <summary>How do you use AI in your projects?</summary>
            <p>
              I use AI as a development tool while maintaining a strong understanding of the code I
              write. I write the majority of my code myself and use AI primarily for repetitive tasks, 
              brainstorming, and debugging. I can also effectively prompt and guide AI coding tools
              when prototyping or adding features, while reviewing and refining the resulting code myself.
            </p>
          </details>
          <details>
            <summary>What kind of projects do you work on?</summary>
            <p>
              I work on a wide range of software projects, including web applications, backend services,
              automation tools, APIs, and game-related projects. I enjoy working across the stack and
              building projects from the initial idea through development and deployment, while conquering
              challenges and learning new technologies along the way.
            </p>
          </details>
          <details>
            <summary>Do you work on both frontend and backend development?</summary>
            <p>
              Yes, I work on both frontend and backend development, and I enjoy building full-stack applications 
              that deliver a seamless user experience.
            </p>
          </details>
        </div>
      </Section>

      <Section eyebrow="Products" title="What we build">
        <ul className="list">
          {products.map((product) => (
            <li key={product.path} style={{ "--accent": product.accent }}>
              <Link to={product.path} className="list-item list-link">
                <div className="list-item-main">
                  
                  <h3><span className="accent-dot" aria-hidden="true" />{product.name}</h3>
                  <p>{product.description}</p>
                </div>
                <div className="list-item-end">
                  <StatusBadge status={product.status} />
                  <ProjectTimeframe product={product} className="list-when" />
                  <Arrow />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </Section>
    </article>
  );
}


function PhotographyPage() {
  const images = [
    {
      path: "/images/photography/mossy.png",
      altText: "A mossy stone",
      title: "Mossy Stone",
      description: "One of my favorite nature images I've taken, I love how the grassy clump of moss looks.",
    },
    {
      path: "/images/photography/mushroomlog.png",
      altText: "Log covered in mushrooms",
      title: "Mushroom Log",
      description: "I love how the green part in the mushrooms complement the moss on the log.",
    },
    {
      path: "/images/photography/watercloseup.png",
      altText: "Water",
      title: "Water Close-up",
      description: "The texture of the water is so captivating here, as it's such a rare sight to see.",
    },
    {
      path: "/images/photography/birdclaws.png",
      altText: "Parrot Claws",
      title: "Parrot Claws",
      description: "The parrot's claws are so pretty, and the blurred background and the colorful feathers add to the overall beauty of the image.",
    },
    {
      path: "/images/photography/animalfriends.png",
      altText: "Two Capybaras",
      title: "Animal Friends",
      description: "Two capybaras sitting together, looking like good friends.",
    },
    {
      path: "/images/photography/Thirds.jpg",
      altText: "Petal Among Rocks",
      title: "Petal Among Rocks",
      description: "A red petal nestled among rocks, capturing a moment of natural beauty.",
    },
  ];

  return (
    <article className="page">
      <Link to="/" className="back-link">
        <span aria-hidden="true">←</span> Home
      </Link>

      <header className="page-header">
        <p className="eyebrow">Photography</p>
        <h1>My Best Shots</h1>
        <p className="lead">
          A collection of my best shots, captured across various times and places.
        </p>
      </header>

      <Section eyebrow="photos" title="Gallery">
        <ZoomableImage
          images={images}
          location="centered"
          wrap={false}
          columns={3}
          minColumnWidth={240}
          imageHeight={260}
        />
      </Section>
    </article>
  );
}


function NotFoundPage() {
  return (
    <section className="page not-found">
      <p className="not-found-code">404</p>
      <h1>Page not found.</h1>
      <p className="lead">
        The page you're looking for doesn't exist, or it moved somewhere else.
      </p>
      <div className="actions">
        <Link to="/" className="button button-primary">
          Back to home <Arrow />
        </Link>
      </div>
    </section>
  );
}

/* ============================================================
   APP
   ============================================================ */

function App() {
  return (
    <BrowserRouter>
      <RouteEffects />

      <div className="site">
        <a href="#main-content" className="skip-link">
          Skip to content
        </a>

        <Header />

        <main id="main-content" className="container" tabIndex={-1}>
          <Routes>
            <Route path="/" element={<HomePage />} />

            {products.map((product) => {
              const Page = product.page;
              return (
                <Route
                  key={product.path}
                  path={product.path}
                  element={<Page product={product} />}
                />
              );
            })}

            <Route path="/about" element={<AboutPage />} />
            {/* secret pages (special stuff) */}
            <Route path="/photography" element={<PhotographyPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>

        <Footer />
      </div>
    </BrowserRouter>
  );
}

export default App;
